/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  updatePrivilegedControls,
} from '@gh/builder-shared';
import { AelEvents, PRIVILEGED_EP_SYS_VARS_ACCOUNTING } from '@gh/builder-shared-data';
import {
  ActionFlow,
  getRouteInitData,
  getSingleNewSelectedEntity,
  resetPrivilegedTokensAndUpdate,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, map$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import {
  AccountingAccount,
  AccountingAccountFinderDialogConfig,
  getAccountingAccountId,
  SystemVarsAccountingBusinessService,
  SystemVarsAccountingReceivableModel,
} from '../../../resources';
import { accountingAccountFinderDialogRoute } from '../accounting-account-dialog';
import { SystemVarsAccountingReceivablesFormService } from './system-vars-accounting-receivables-form.service';

@Component({
  standalone: false,
  selector: 'gh-system-vars-accounting-receivables-subtab',
  templateUrl: 'system-vars-accounting-receivables-subtab.component.html',
  styleUrls: ['system-vars-accounting-receivables-subtab.component.scss'],
  viewProviders: [
    SystemVarsAccountingReceivablesFormService,
  ],
})
export class SystemVarsAccountingReceivablesSubtabComponent
  extends AbstractFormComponent<SystemVarsAccountingReceivableModel>
  implements OnInit {
  private originalReceivables: SystemVarsAccountingReceivableModel;
  private isModifyMode: boolean = false;

  constructor(
    public form: SystemVarsAccountingReceivablesFormService,
    private route: ActivatedRoute,
    private systemVarsBusiness: SystemVarsAccountingBusinessService,
    private userContext: BuilderUserContext,
    private navigationService: BuilderNavigationService,
    private dialogService: DialogService,
    private pipelines: FormActionPipelines,
    private actions: CommonActions,
  ) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<SystemVarsAccountingReceivableModel>> {
    return this.pipelines.complete({
      execute: this.actions.execute((state: FormFlowState<SystemVarsAccountingReceivableModel>) =>
        this.isModifyMode
          ? this.systemVarsBusiness.modifyAccountingReceivables(state.form.submitValue)
          : this.systemVarsBusiness.updateAccountingReceivables(state.form.submitValue)),
      finish: (state: FormFlowState<SystemVarsAccountingReceivableModel>) => {
        const { value } = state;
        this.originalReceivables = value;
        this.form.build(value);
      },
    });
  }

  ngOnInit(): void {
    const { originalReceivables } = getRouteInitData(this.route);
    assign(this, { originalReceivables });
    this.form.build(originalReceivables);
  }

  onReset(): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        resetPrivilegedTokensAndUpdate(this.form);
        this.form.build(this.originalReceivables);
      });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedAccountingReceivablesWip],
      label: label ? label : 'lbl_menu_accounting',
    });
  }

  onPrivilegedTokenReceived(): void {
    updatePrivilegedControls(this.form, PRIVILEGED_EP_SYS_VARS_ACCOUNTING);
    this.isModifyMode = true;
  }

  getMapValue(item: AccountingAccount): string {
    const code = item.code || '';
    const name = item.name || '';

    return [code, name].join(' ').trim();
  }

  onAccountFind(fieldName: string, accountClass?: string): void {
    const assignedAccount = this.form.getAccount(fieldName);

    this.dialogService.openWithInput(accountingAccountFinderDialogRoute, <AccountingAccountFinderDialogConfig>{
        accountClass,
        preselected: assignedAccount ? [getAccountingAccountId(assignedAccount)] : void 0,
      })
      .afterClosed().pipe(
      filter$(Boolean),
      map$(getSingleNewSelectedEntity))
      .subscribe((account: AccountingAccount) => this.form.setAccount(fieldName, account));
  }
}
